import {
  call, put, takeLatest, select, take, fork,
} from 'redux-saga/effects';
import api from '@/api/user';
import userUtils from '@/utils/user';
import authUtils from '@/utils/auth';

import {
  ANALYTICS_SEND_EVENT,
} from '@/action-types/analytics';
import {
  ERROR_PASS_REQUEST,
  ERROR_UPDATE_REQUEST,
  ERROR_RESET_REQUEST,
  VALIDATION_ERROR_REQUEST,
} from '@/action-types/error';
import {
  LOADING_UPDATE_REQUEST,
} from '@/action-types/loading';
import {
  LOGIN_RESET_STATE_REQUEST,
  LOGIN_AUTH_UPDATE_ACCESS,
} from '@/action-types/login';
import {
  STUDENT_GET_INFO_REQUEST,
  STUDENT_REGISTRATION_REQUEST,
  STUDENT_REGISTRATION_SUCCESS,
  STUDENT_GET_INFO_SUCCESS,
} from '@/action-types/student';
import {
  USER_REGISTRATION_REQUEST,
  USER_REGISTRATION_PAY_REQUEST,
  USER_UPDATE_PROFILE_REQUEST,
  USER_UPDATE_PROFILE_SUCCESS,
  USER_UPDATE_PROFILE_ERROR,
  USER_SEND_FEEDBACK_REQUEST,
  USER_SEND_FEEDBACK_SUCCESS,
} from '@/action-types/user';

export interface AvatarData {
  file?: File;
  json?: string;
}
export interface UserRegistraionData {
  role: string;
  skype?: string;
  name: string;
  avatarFile?: File;
  avatarLink?: string;
  phone?: string;
  englishLevel?: number;
  notFree: boolean;
}

export interface UserUpdateData {
  skype?: string;
  name?: string;
  avatarFile?: File;
  phone?: string;
  password?: string;
}

export function* registrationSuccess(params: {
  data: {
    role: string;
  };
}) {
  if (params.data.role === 'student') {
    yield put({ type: STUDENT_GET_INFO_REQUEST });
    yield take(STUDENT_GET_INFO_SUCCESS);
    yield put({
      type: STUDENT_REGISTRATION_REQUEST,
      pathname: window.location.pathname,
    });
    yield take(STUDENT_REGISTRATION_SUCCESS);
  }

  yield put({ type: LOGIN_AUTH_UPDATE_ACCESS });
  yield put({ type: LOGIN_RESET_STATE_REQUEST });
}

export function* registration(params: { data: UserRegistraionData; }) {
  try {
    yield put({ type: LOADING_UPDATE_REQUEST, name: 'user', state: true });

    const { user } = yield select();

    const userData = {
      name: params.data.name,
      ...(params.data.notFree ? { notFree: true } : {}),
      ...(params.data.skype ? { skype: params.data.skype } : {}),
    };

    const avatarData = {
      file: params.data.avatarFile,
      json: userUtils.createAvatarJson(params.data, user.uid),
    };

    // @ts-ignore
    yield call(api.registration, params.data.role, userData, avatarData);
    yield fork(registrationSuccess, params);
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'registration'] });
    yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: e });

    let errorMsg : string | undefined;

    if (e.graphQLErrors) {
      e.graphQLErrors.forEach((error: {
        extensions: {
          detail: string;
        };
      }) => {
        switch (error.extensions.detail) {
          case 'An image allowed type jpg or png.':
            errorMsg = error.extensions.detail;
            break;
          default:
          // nothing
        }
      });
    }

    if (errorMsg) {
      yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: e });
      yield take(ERROR_RESET_REQUEST);
      yield fork(registrationSuccess, params);
    } else {
      yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: e });
    }
  } finally {
    yield put({ type: LOADING_UPDATE_REQUEST, name: 'user', state: false });
  }
}

export function* registrationPay(params: {
  data: UserRegistraionData;
  // Якщо реєстрація з форми - відправляєво валідаційну помилку, якщо з попапу - звичайну
  hasValidation?: boolean;
}) {
  try {
    yield put({ type: LOADING_UPDATE_REQUEST, name: 'user', state: true });

    const { user } = yield select();

    const userData = {
      name: params.data.name,
      ...(params.data.phone ? { phone: params.data.phone } : {}),
      ...(params.data.englishLevel ? { englishLevel: `api/english_levels/${params.data.englishLevel}` } : {}),
      notFree: true,
    };

    const avatarData = {
      file: params.data.avatarFile,
      json: userUtils.createAvatarJson(params.data, user.uid),
    };

    // @ts-ignore
    yield call(api.registration, 'student', userData, avatarData);
    yield fork(registrationSuccess, params);
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'registrationPay'] });

    if (params.hasValidation) {
      yield put({ type: VALIDATION_ERROR_REQUEST, isActive: true, errorData: e });
    } else {
      yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: e });
    }

    let errorMsg : string | undefined;

    if (e.graphQLErrors) {
      e.graphQLErrors.forEach((error: {
        extensions: {
          detail: string;
        };
      }) => {
        switch (error.extensions.detail) {
          case 'An image allowed type jpg or png.':
            errorMsg = error.extensions.detail;
            break;
          default:
          // nothing
        }
      });
    }

    if (errorMsg) {
      yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: e });
      yield take(ERROR_RESET_REQUEST);
      yield fork(registrationSuccess, params);
    } else {
      yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: e });
    }
  } finally {
    yield put({ type: LOADING_UPDATE_REQUEST, name: 'user', state: false });
  }
}

export function* update(params: { data: UserUpdateData; }) {
  yield put({ type: LOADING_UPDATE_REQUEST, name: 'updateProfile', state: true });

  const {
    user: { uid, role },
    teacher,
    student,
  } = yield select();

  const teacherId = teacher.data.id;
  const studentId = student.data.id;

  const avatarData = {
    file: params.data.avatarFile,
    json: userUtils.createAvatarJson({ avatarFile: params.data.avatarFile }, uid),
  };
  const userData : {
    name?: string;
    skype?: string;
    password?: string;
    phone?: string;
  } = {
    name: student.data.name,
  };

  if (params.data.name !== undefined) userData.name = params.data.name;
  if (params.data.skype !== undefined) userData.skype = params.data.skype;
  if (params.data.phone !== undefined && params.data.phone !== null) {
    userData.phone = params.data.phone;
  }

  try {
    if (userData.name || userData.skype || userData.phone || params.data.avatarFile) {
      yield call(
        api.update,
        role === 'teacher' ? teacherId : studentId,
        role,
        userData,
        avatarData,
      );
    }

    if (params.data.password) {
      yield call(api.updatePassword, params.data.password, uid);
    }

    yield put({ type: USER_UPDATE_PROFILE_SUCCESS });

    if (params.data.avatarFile) {
      yield put({ type: ANALYTICS_SEND_EVENT, eventName: 'update-avatar' });
    }
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'update'] });
    yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: e });
    yield put({ type: USER_UPDATE_PROFILE_ERROR });
  } finally {
    yield put({ type: LOADING_UPDATE_REQUEST, name: 'updateProfile', state: false });
  }
}

export function* sendFeedback(params: { data: { message: string; image: Blob; };}) {
  const { user, app } = yield select();

  try {
    yield put({ type: LOADING_UPDATE_REQUEST, name: 'sendFeedback', state: true });

    const token = authUtils.getAccessToken();
    const userData = authUtils.convertToken(token);

    let module;

    userData.roles.forEach((el: string) => {
      if (el === 'ROLE_TEACHER_PAY') {
        module = 'ed-teacher';
      } else if (el === 'ROLE_STUDENT_PAY') {
        module = 'ed-student';
      } else if (el === 'ROLE_STUDENT_B2B') {
        module = 'ed-student-b2b';
      }
    });

    yield call(
      api.sendFeedback,
      user.username,
      params.data.message,
      user.role,
      user.uid,
      module,
      app.feedback.screenshotBlob || undefined,
    );

    yield put({ type: ANALYTICS_SEND_EVENT, eventName: 'send-feedback' });
    yield put({ type: USER_SEND_FEEDBACK_SUCCESS });
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'sendFeedback'] });
  } finally {
    yield put({ type: LOADING_UPDATE_REQUEST, name: 'sendFeedback', state: false });
  }
}

export default function* userSaga() {
  // экшн ниже вызывается когда юзер регается через попап
  // @ts-ignore
  yield takeLatest(USER_REGISTRATION_REQUEST, registration);
  // @ts-ignore
  yield takeLatest(USER_REGISTRATION_PAY_REQUEST, registrationPay);
  // @ts-ignore
  yield takeLatest(USER_UPDATE_PROFILE_REQUEST, update);
  // @ts-ignore
  yield takeLatest(USER_SEND_FEEDBACK_REQUEST, sendFeedback);
}
